import {
  useCreatePaymentChannel,
  useUpdatePaymentChannel,
} from "@features/cx/settings/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useEffect, useState } from "react";

interface PaymentChannel {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  processingFee?: number;
  remark?: string;
}

interface PaymentChannelFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (channelData: Omit<PaymentChannel, "code" | "isActive">) => void;
  editingChannel?: PaymentChannel | null;
}

export function PaymentChannelForm({
  isOpen,
  onClose,
  onSave,
  editingChannel,
}: PaymentChannelFormProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const createPaymentChannel = useCreatePaymentChannel();
  const updatePaymentChannel = useUpdatePaymentChannel();
  const [formData, setFormData] = useState({
    name: editingChannel?.name || "",
    type: editingChannel?.type || "",
    processingFee: editingChannel?.processingFee || 0,
    remark: editingChannel?.remark || "",
  });

  useEffect(() => {
    setFormData({
      name: editingChannel?.name || "",
      type: editingChannel?.type || "",
      processingFee: editingChannel?.processingFee || 0,
      remark: editingChannel?.remark || "",
    });
  }, [editingChannel, isOpen]);

  // Form field definitions
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Channel Name",
      placeholder: "Enter channel name",
      required: true,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "Enter a descriptive name for this payment channel (e.g., 'Walk-in Counter', 'Online Portal', 'Mobile App'). This identifies how customers can make payments through this channel."
    },
    {
      name: "processingFee",
      type: "number",
      label: "Processing Fee (%)",
      placeholder: "0.0",
      min: 0,
      step: 0.1,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "Set the processing fee percentage for this channel. This fee will be applied to transactions processed through this channel. Enter 0 for no processing fee (e.g., cash payments)."
    },
    {
      name: "remark",
      type: "textarea",
      label: "Description",
      placeholder: "Enter channel description",
      rows: 3,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "Provide a detailed description of this payment channel. Include information about how customers can use this channel, any special requirements, or processing times."
    },
  ];

  // Create form using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  // Reset form when formData changes
  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    const operation = editingChannel ? "update" : "create";
    switch (operation) {
      case "update":
        const payloadEdit = {
          name: formData.name,
          code: editingChannel?.code,
          processing_fee: formData.processingFee,
          remark: formData.remark,
          remote_utility_id: Number(remoteUtilityId),
        };
        updatePaymentChannel.mutateAsync(payloadEdit);
        break;
      case "create":
        const payloadAdd = {
          name: formData.name,
          code: formData.name,
          processing_fee: formData.processingFee,
          remark: formData.remark,
          remote_utility_id: Number(remoteUtilityId),
        };
        createPaymentChannel.mutateAsync(payloadAdd);
        break;
    }
    handleClose();
  };

  // Handle form reset
  const handleReset = () => {
    const resetData = {
      name: "",
      type: "",
      processingFee: 0,
      remark: "",
    };
    setFormData(resetData);
    form.reset(resetData);
    handleClose();
  };

  // Handle dialog close
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingChannel ? "Edit Payment Channel" : "Add Payment Channel"}
          </DialogTitle>
          <DialogDescription>
            Configure the payment channel settings and processing fees.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            gridColumns={1}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: editingChannel
                ? "Update Channel"
                : "Add Channel",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
